/**
 * The application's stylesheet is linked in the page's head and blocks the first
 * paint, but a request can still fail (a network or proxy hiccup, a deploy in
 * progress, an expired session answering with a login page). The browser then
 * shows the page unstyled: a white page, images at their natural size (a flag
 * across the screen). Before the first render, check that the stylesheet has
 * applied; if not, fetch it again once, then reload the page once, then say so
 * plainly rather than showing a broken page.
 */
import { reloadOnceForNewBuild } from "./lazyRoute";

/** Declared on `:root` by `index.css`. */
const SENTINEL = "--nexus-styles";
const RETRY_TIMEOUT_MS = 8000;

export function stylesApplied(root: Element = document.documentElement): boolean {
  return getComputedStyle(root).getPropertyValue(SENTINEL).trim() === "ready";
}

/** The same stylesheet, bypassing any cache that kept a failed or wrong answer. */
export function retryHref(href: string, now = Date.now()): string {
  const url = new URL(href, window.location.href);
  url.searchParams.set("retry", String(now));
  return url.toString();
}

function loadStylesheet(href: string): Promise<boolean> {
  return new Promise((resolve) => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    const timer = window.setTimeout(() => resolve(false), RETRY_TIMEOUT_MS);
    link.onload = () => {
      window.clearTimeout(timer);
      resolve(true);
    };
    link.onerror = () => {
      window.clearTimeout(timer);
      link.remove();
      resolve(false);
    };
    document.head.appendChild(link);
  });
}

/**
 * Resolves `true` once the stylesheet applies (at once in the usual case),
 * `false` when the page is being reloaded or the styles could not be loaded.
 */
export async function ensureStylesheets(): Promise<boolean> {
  if (stylesApplied()) return true;
  const links = [...document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]')].filter(
    (link) => link.href,
  );
  await Promise.all(links.map((link) => loadStylesheet(retryHref(link.href))));
  if (stylesApplied()) return true;
  if (reloadOnceForNewBuild()) return false;
  showStylesUnavailable();
  return false;
}

/** A plain, self-styled notice in place of an unstyled application. */
function showStylesUnavailable() {
  const root = document.getElementById("root");
  if (!root) return;
  root.innerHTML = `
    <div role="alert" style="min-height:100vh;display:flex;align-items:center;justify-content:center;padding:0 24px;text-align:center;font:15px/1.5 Inter,ui-sans-serif,system-ui,sans-serif;color:#5b6474">
      <p style="margin:0;max-width:28rem">The page could not load its styles. <a href="" style="color:#0a7bbd">Reload the page</a>; if this keeps happening, check your connection.</p>
    </div>`;
}
